import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import AppApi from '@/apis/app-api';
import OfflineShopsApi, {
  BatchUpdateOfflineShopsDto,
  CreateOfflineShopDto,
  UpdateOfflineShopBodyDto,
  UpdateOfflineShopParamsDto,
} from '@/apis/offline-shops.api';
import { SHOP_IS_ACTIVE } from '@/consts/enum';
import { baseUrl } from '@/consts/route';
import { OfflineShop } from '@/types/naver-review';

import { useAuth } from './useAuth';

export const mockOfflineShops = [
  {
    offline_shop_id: '72d15442-8d06-49b9-8919-004db904a0f8',
    shop_name: '신동궁감자탕 역삼직영점',
    shop_address: '서울특별시 강남구 역삼동 823-36 1층 신동궁감자탕뼈숯불구이',
    place_id: '11832088',
    is_active: 1,
    created_at: '2024-11-03T07:42:59.000Z',
    updated_at: '2024-11-03T07:46:06.000Z',
  },
  {
    offline_shop_id: '72d15442-8d06-49b9-8919-004db904a0f9',
    shop_name: '유래등',
    shop_address: '경기 구리시 동구릉로 239',
    place_id: '17850012',
    is_active: 1,
    created_at: '2024-11-04T01:15:39.000Z',
    updated_at: '2024-11-04T01:15:39.000Z',
  },
];

export const useGetOfflineShops = (is_only_active: boolean = true) => {
  const { userId, accessToken } = useAuth();

  const getUsersOfflineShop = async () => {
    if (!userId) return [];
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const shops = await offlineShopsApi.getShops({
      user_id: userId,
      is_only_active: is_only_active,
    });

    return shops as OfflineShop[];
  };

  return useQuery({
    queryKey: ['offlineShops', userId, is_only_active],
    queryFn: getUsersOfflineShop,
    enabled: !!userId,
  });
};

// > 이전 부분제거하고 나머지 저장
export const useCreateOfflineShopBatch = () => {
  const { userId, accessToken } = useAuth();

  const createOfflineShop = async (createOfflineShopDtos: CreateOfflineShopDto[]) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.createShopBatch(userId, createOfflineShopDtos);

    return res;
  };

  return useMutation({
    mutationKey: ['createOfflineShop', userId],
    mutationFn: createOfflineShop,
  });
};

export const useRegisterBatch = () => {
  const { userId, accessToken } = useAuth();

  const createOfflineShop = async (offline_shop_ids: string[]) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.registerBatch(userId, {
      offline_shop_ids,
    });

    return res;
  };

  return useMutation({
    mutationKey: ['createOfflineShop', userId],
    mutationFn: createOfflineShop,
  });
};

export const useRequestCrawlingBatch = () => {
  const { userId, accessToken } = useAuth();
  const queryClient = useQueryClient();

  const createOfflineShop = async (offline_shop_ids: string[]) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.requestCrawlingBatch(userId, {
      offline_shop_ids,
    });

    return res;
  };

  return useMutation({
    mutationKey: ['createOfflineShop', userId],
    mutationFn: createOfflineShop,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['offlineReviews'],
        exact: false,
      });

      await queryClient.refetchQueries({
        queryKey: ['offlineReviewsSummary'],
        exact: false,
      });
    },
  });
};

export const useUpdateOfflineShop = () => {
  const { userId, accessToken } = useAuth();
  const queryClient = useQueryClient();

  const updateOfflineShop = async (
    updateOfflineShopBodyDto: UpdateOfflineShopBodyDto &
      Omit<UpdateOfflineShopParamsDto, 'user_id'>,
  ) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.updateShop(
      {
        offline_shop_id: updateOfflineShopBodyDto.offline_shop_id,
        user_id: userId,
      },
      updateOfflineShopBodyDto,
    );

    return res;
  };

  return useMutation({
    mutationKey: ['updateOfflineShop', userId],
    mutationFn: updateOfflineShop,
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['offlineShops'],
        exact: false,
      });
    },
  });
};

export const useBatchUpdateOfflineShop = () => {
  const { userId, accessToken } = useAuth();

  const batchUpdateOfflineShop = async (batchUpdateOfflineShopsDto: BatchUpdateOfflineShopsDto) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.batchUpdateShop({
      user_id: userId,
      batchUpdateOfflineShopsDto,
    });

    return res;
  };

  return useMutation({
    mutationKey: ['batchUpdateOfflineShop', userId],
    mutationFn: batchUpdateOfflineShop,
  });
};

export const useDeleteOfflineShop = () => {
  const { userId, accessToken } = useAuth();
  const queryClient = useQueryClient();

  const deleteOfflineShop = async (offlineShopId: string) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.deleteShop({
      offline_shop_id: offlineShopId,
      user_id: userId,
      hard_delete: true,
    });

    return res;
  };

  return useMutation({
    mutationFn: deleteOfflineShop,
    mutationKey: ['deleteOfflineShop', userId],
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['offlineShops'],
        exact: false,
      });
    },
  });
};

export const useInactiveOfflineShop = () => {
  const { userId, accessToken } = useAuth();
  const queryClient = useQueryClient();

  const inactiveOfflineShop = async (offlineShopId: string) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.updateShop(
      {
        offline_shop_id: offlineShopId,
        user_id: userId,
      },
      {
        is_active: SHOP_IS_ACTIVE.INACTIVE,
      },
    );

    return res;
  };

  return useMutation({
    mutationFn: inactiveOfflineShop,
    mutationKey: ['inactiveOfflineShop', userId],
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['offlineShops'],
        exact: false,
      });
    },
  });
};

export const useCreateOfflineShop = () => {
  const { userId, accessToken } = useAuth();

  const createOfflineShop = async ({
    shop_name,
    shop_address,
    category,
    url = '',
    crawl = false,
  }: CreateOfflineShopDto) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.createShop(userId, {
      shop_name,
      shop_address,
      category,
      url,
      crawl,
    });

    return res;
  };

  return useMutation({
    mutationFn: createOfflineShop,
    mutationKey: ['createOfflineShop', userId],
  });
};

export const useRequestCrawling = () => {
  const { userId, accessToken } = useAuth();
  const queryClient = useQueryClient();

  const requestCrawlingShop = async (offline_shop_id: string) => {
    const baseApi = new AppApi(baseUrl);

    baseApi.setup(accessToken);

    const offlineShopsApi = new OfflineShopsApi(baseApi);
    const res = await offlineShopsApi.crawlingShop(userId, offline_shop_id);

    return res;
  };

  return useMutation({
    mutationFn: requestCrawlingShop,
    mutationKey: ['crawlingofflineshop', userId],
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['offlineReviews'],
        exact: false,
      });
    },
  });
};
